// Per-connection cache of ParsedBuffer entries keyed by documentId.
// Owns the LRU eviction policy + the incremental-edit re-parse path.
// `change(id, newText)` = full replacement; `applyEdits(id, edits)` = edit-aware.
// LRU: insertion order in the underlying Map is the recency dimension; every
// open/change/applyEdits/get re-inserts to keep the entry at the tail.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § "Service Layer — AST Cache".

import type { ParseOptions, ParsedDocument } from '../../sql-grammar/model';
import type { ParsedBuffer, ScriptScope, SchemaSnapshot } from '../types';
import { getDocumentStructure } from '../../sql-grammar/document';

import {
  applyEdits as applyEditsToText,
  parseIncremental,
  type TextEdit,
} from './parseIncremental';

export type BuildParsedDocumentFn = (
  source: string,
  options: ParseOptions,
) => Promise<ParsedDocument> | ParsedDocument;

export type DocumentCacheConfig = {
  readonly maxCachedDocs?: number;
};

export const DEFAULT_MAX_CACHED_DOCS = 16;

export type OpenDocumentInput = {
  readonly documentId: string;
  readonly text: string;
  readonly options: ParseOptions;
  readonly scriptScope?: ScriptScope;
  readonly schemaSnapshot?: SchemaSnapshot;
};

export type ChangeOptions = {
  readonly scriptScope?: ScriptScope;
  readonly schemaSnapshot?: SchemaSnapshot;
};

type Entry = {
  readonly buffer: ParsedBuffer;
  readonly text: string;
  readonly options: ParseOptions;
  readonly scriptScope?: ScriptScope;
};

export type DocumentCache = {
  readonly open: (input: OpenDocumentInput) => Promise<ParsedBuffer>;
  readonly change: (
    documentId: string,
    newText: string,
    opts?: ChangeOptions,
  ) => Promise<ParsedBuffer>;
  readonly applyEdits: (
    documentId: string,
    edits: readonly TextEdit[],
    opts?: ChangeOptions,
  ) => Promise<ParsedBuffer>;
  readonly close: (documentId: string) => void;
  readonly clear: () => void;
  readonly get: (documentId: string) => ParsedBuffer | undefined;
  readonly size: () => number;
  readonly rebindSnapshot: (snapshot: SchemaSnapshot | undefined) => void;
  readonly documentIds: () => readonly string[];
};

export function createDocumentCache(
  buildParsedDocument: BuildParsedDocumentFn,
  config?: DocumentCacheConfig,
): DocumentCache {
  const maxCached = Math.max(1, config?.maxCachedDocs ?? DEFAULT_MAX_CACHED_DOCS);
  const entries = new Map<string, Entry>();

  function touch(id: string, value: Entry): void {
    entries.delete(id);
    entries.set(id, value);
    while (entries.size > maxCached) {
      const oldest = entries.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      entries.delete(oldest);
    }
  }

  async function buildFull(
    documentId: string,
    text: string,
    options: ParseOptions,
    scriptScope: ScriptScope | undefined,
    schemaSnapshot: SchemaSnapshot | undefined,
  ): Promise<ParsedBuffer> {
    const parsed = await Promise.resolve(buildParsedDocument(text, options));
    const structure = getDocumentStructure(parsed, documentId);
    const buffer: ParsedBuffer = {
      parsedDocument: parsed,
      documentStructure: structure,
      schemaSnapshot,
      scriptScope,
    };
    touch(documentId, { buffer, text, options, scriptScope });
    return buffer;
  }

  async function buildIncremental(
    documentId: string,
    edits: readonly TextEdit[],
    existing: Entry,
    scriptScope: ScriptScope | undefined,
    schemaSnapshot: SchemaSnapshot | undefined,
  ): Promise<ParsedBuffer> {
    const newText = applyEditsToText(existing.text, edits);
    const parsed = await Promise.resolve(
      parseIncremental(
        existing.buffer.parsedDocument,
        edits,
        newText,
        existing.options,
        buildParsedDocument,
      ),
    );
    const structure = getDocumentStructure(parsed, documentId);
    const buffer: ParsedBuffer = {
      parsedDocument: parsed,
      documentStructure: structure,
      schemaSnapshot,
      scriptScope,
    };
    touch(documentId, { buffer, text: newText, options: existing.options, scriptScope });
    return buffer;
  }

  return {
    async open(input) {
      return buildFull(
        input.documentId,
        input.text,
        input.options,
        input.scriptScope,
        input.schemaSnapshot,
      );
    },
    async change(documentId, newText, opts) {
      const existing = entries.get(documentId);
      if (!existing) {
        throw new Error(`documentCache.change: unknown documentId '${documentId}'`);
      }
      const scope = opts?.scriptScope ?? existing.scriptScope;
      const snap = opts?.schemaSnapshot ?? existing.buffer.schemaSnapshot;
      return buildFull(documentId, newText, existing.options, scope, snap);
    },
    async applyEdits(documentId, edits, opts) {
      const existing = entries.get(documentId);
      if (!existing) {
        throw new Error(`documentCache.applyEdits: unknown documentId '${documentId}'`);
      }
      const scope = opts?.scriptScope ?? existing.scriptScope;
      const snap = opts?.schemaSnapshot ?? existing.buffer.schemaSnapshot;
      return buildIncremental(documentId, edits, existing, scope, snap);
    },
    close(documentId) {
      entries.delete(documentId);
    },
    clear() {
      entries.clear();
    },
    get(documentId) {
      const entry = entries.get(documentId);
      if (!entry) return undefined;
      // LRU bump: re-insert keeps the entry at the tail of the eviction queue.
      entries.delete(documentId);
      entries.set(documentId, entry);
      return entry.buffer;
    },
    size() {
      return entries.size;
    },
    rebindSnapshot(snapshot) {
      for (const [id, entry] of entries) {
        entries.set(id, {
          ...entry,
          buffer: { ...entry.buffer, schemaSnapshot: snapshot },
        });
      }
    },
    documentIds() {
      return Array.from(entries.keys());
    },
  };
}
